class ComponentVideo extends Theme.Component {
  connectedCallback() {
    this.defineElements({
      video: "video",
      button: "button",
      poster: "img"
    });

    super.connectedCallback();

    if (this.elements.video) {
      this.watchPlayback();
    }

    if (this.data.desktop_preload) {
      this.setupDesktopPreload();
    }

    if (this.elements.video?.autoplay) {
      return;
    }

    this.intersectionObserver((entry) => {
      if (!entry.isIntersecting) {
        this.pause();
      } else if (this.hasAttribute("active")) {
        this.play();
      }
    });

    if (this.hasAttribute("active")) {
      this.play();
    }
  }

  static get observedAttributes() {
    return ["active"];
  }

  attributeChangedCallback() {
    if (!this.isConnected) {
      return;
    }

    if (this.hasAttribute("active")) {
      this.play();
    } else {
      this.pause();
    }
  }

  setupDesktopPreload() {
    const desktop = window.matchMedia("(min-width: 768px)");

    let nearby = false;

    const preload = (mode) => {
      if (!desktop.matches) {
        return;
      }

      this.lazyLoad(mode);

      if (this.elements.video.preload !== "auto") {
        this.elements.video.preload = mode;
      }
    };

    this.intersectionObserver((entry) => {
      nearby = entry.isIntersecting;

      if (nearby) {
        preload("metadata");
      }
    }, { rootMargin: "200px 0px", threshold: 0 });

    this.addDirectEvent("pointerenter", this, () => preload("auto"));
    this.addDirectEvent("focusin", this, () => preload("auto"));

    this.addDirectEvent("change", desktop, () => {
      if (!desktop.matches && this.elements.video) {
        this.elements.video.preload = "none";
      } else if (nearby) {
        preload("metadata");
      }
    });
  }

  lazyLoad(preload = "none") {
    if (this.elements.video || !this.data.lazy) {
      return;
    }

    const video = document.createElement("video");

    video.poster = this.elements.poster.src;
    video.preload = preload;
    video.playsInline = true;

    for (const attribute in this.data.attributes) {
      video[attribute] = this.data.attributes[attribute];
    }

    this.data.sources.forEach((source) => {
      const element = document.createElement("source");

      element.src = source.url;
      element.type = source.mime_type;

      video.appendChild(element);
    });

    this.elements.poster.insertAdjacentElement("afterend", video);
    this.defineElements({ video: "video" });
    this.watchPlayback();
  }

  watchPlayback() {
    this.addDirectEvent("playing", this.elements.video, () => this.syncPlayback(true));
    this.addDirectEvent("pause", this.elements.video, () => this.syncPlayback(false));
    this.addDirectEvent("ended", this.elements.video, () => this.syncPlayback(false));
    this.addDirectEvent("error", this.elements.video, () => this.syncPlayback(false));
  }

  syncPlayback(playing) {
    this.toggleAttribute("playing", playing);
    this.elements.button?.setAttribute("aria-label", playing ? "Pause video" : "Play video");
  }

  toggle() {
    if (this.elements.video && !this.elements.video.paused) {
      this.pause();
    } else {
      this.play();
    }
  }

  play() {
    this.lazyLoad();

    if (!this.elements.video) {
      return;
    }

    this.pauseAll();

    this.elements.video.play().catch(() => {
      this.syncPlayback(!this.elements.video.paused);
    });
  }

  pause() {
    this.elements.video?.pause();
    this.syncPlayback(false);
  }

  pauseAll() {
    document.querySelectorAll("component-video").forEach((component) => {
      if (component !== this) {
        component.pause();
      }
    });
  }

  disconnectedCallback() {
    this.pause();
    super.disconnectedCallback();
  }
}

customElements.define("component-video", ComponentVideo);
